import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiHelperService } from 'app/shared/services/api/api-helper.service';
import { ApiService } from 'app/shared/services/api/api.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MembershipLevel, MembershipLevelStatus } from '../models/membership-level.model';

/**
 * Builds a user-visible message from membership API error payloads.
 * `create` / `update` / `delete` use {@link MembershipLevelService.handleError}, which often rethrows `error.errors`.
 */
export function formatMembershipLevelApiError(error: unknown, fallback: string): string {
  const fromItems = (items: Array<Record<string, unknown>>): string | null => {
    const parts = items
      .map(e => String(e['details'] ?? e['Details'] ?? e['message'] ?? e['Message'] ?? '').trim())
      .filter(Boolean);
    return parts.length ? parts.join(' ') : null;
  };

  if (Array.isArray(error)) {
    const msg = fromItems(error as Array<Record<string, unknown>>);
    if (msg) return msg;
  }

  const httpErr = error as HttpErrorResponse;
  const body = httpErr?.error;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const raw = (record['errors'] ?? record['Errors']) as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(raw) && raw.length > 0) {
      const msg = fromItems(raw);
      if (msg) return msg;
    }
    const single = String(
      record['details'] ?? record['Details'] ?? record['message'] ?? record['Message'] ?? '',
    ).trim();
    if (single) return single;
  }

  return fallback;
}

export interface BulkMembershipCategoryOperationResult {
  successCount: number;
  errors: { membershipCategoryId: string; message: string }[];
}

export interface MembershipCategoryImportResult {
  importedCount: number;
  errors: { row: number; message: string }[];
}

/**
 * Membership levels API (maps PascalCase JSON to `MembershipLevel` view models).
 * Uses `ApiService` like Accounts; CSV upload uses raw `HttpClient` + `apiOrigin` like account import.
 */
@Injectable({ providedIn: 'root' })
export class MembershipLevelService {
  /**
   * Path only — `ApiService` prepends `apiHelper.apiUrl`. Do not embed `apiUrl` here or the URL is doubled
   * (e.g. `https://hosthttps://host/api/...`) and XHR throws `Invalid URL`.
   */
  private readonly apiBasePath = '/api/Membership';

  /** Used for raw `HttpClient` import upload (must be absolute when dev server host ≠ API host). */
  private readonly apiOrigin: string;

  constructor(
    private readonly api: ApiService,
    private readonly http: HttpClient,
    { apiUrl }: ApiHelperService,
  ) {
    this.apiOrigin = apiUrl ?? '';
  }

  getAll(propertyId: string): Observable<MembershipLevel[]> {
    return this.api.get(`${this.apiBasePath}/property/${propertyId}`).pipe(
      map(response => (response.data as unknown[]).map(r => this.mapLevel(r))),
      catchError(err => this.handleError(err)),
    );
  }

  create(
    propertyId: string,
    draft: Omit<MembershipLevel, 'membershipCategoryId'>,
  ): Observable<MembershipLevel> {
    return this.api
      .post(`${this.apiBasePath}/property/${propertyId}`, {
        name: draft.name,
        description: draft.description,
        duesFrequency: draft.duesFrequency,
        duesAmount: draft.duesAmount,
        status: draft.status,
        activeMemberCount: draft.activeMembers,
      })
      .pipe(
        map(response => this.mapLevel(response.data)),
        catchError(err => this.handleError(err)),
      );
  }

  update(propertyId: string, level: MembershipLevel): Observable<MembershipLevel> {
    return this.api
      .put(`${this.apiBasePath}/property/${propertyId}/${level.membershipCategoryId}`, {
        membershipCategoryId: level.membershipCategoryId,
        name: level.name,
        description: level.description,
        duesFrequency: level.duesFrequency,
        duesAmount: level.duesAmount,
        status: level.status,
        activeMemberCount: level.activeMembers,
      })
      .pipe(
        map(response => this.mapLevel(response.data)),
        catchError(err => this.handleError(err)),
      );
  }

  delete(
    propertyId: string,
    membershipCategoryId: string,
  ): Observable<{ ok: boolean; blockedByActiveMembers: boolean }> {
    return this.api
      .post(`${this.apiBasePath}/property/${propertyId}/delete/${membershipCategoryId}`, {})
      .pipe(
        map(response => this.mapDeleteResult(response.data)),
        catchError(err => this.handleError(err)),
      );
  }

  bulkUpdateStatus(
    propertyId: string,
    membershipCategoryIds: string[],
    status: MembershipLevelStatus,
  ): Observable<BulkMembershipCategoryOperationResult> {
    return this.api
      .post(`${this.apiBasePath}/property/${propertyId}/bulk/status`, {
        membershipCategoryIds,
        status,
      })
      .pipe(
        map(response => this.mapBulkResult(response.data)),
        catchError(err => this.handleError(err)),
      );
  }

  bulkDelete(propertyId: string, membershipCategoryIds: string[]): Observable<BulkMembershipCategoryOperationResult> {
    return this.api
      .post(`${this.apiBasePath}/property/${propertyId}/bulk/delete`, { membershipCategoryIds })
      .pipe(
        map(response => this.mapBulkResult(response.data)),
        catchError(err => this.handleError(err)),
      );
  }

  private mapDeleteResult(raw: unknown): { ok: boolean; blockedByActiveMembers: boolean } {
    const r = raw as Record<string, unknown>;
    const ok = !!(r['ok'] ?? r['Ok']);
    const blockedByActiveMembers = !!(r['blockedByActiveMembers'] ?? r['BlockedByActiveMembers']);
    return { ok, blockedByActiveMembers };
  }

  private mapBulkResult(raw: unknown): BulkMembershipCategoryOperationResult {
    const r = raw as Record<string, unknown>;
    const successCount = Number(r['successCount'] ?? r['SuccessCount'] ?? 0);
    const rawErrs = (r['errors'] ?? r['Errors']) as Array<Record<string, unknown>> | undefined;
    const arr = Array.isArray(rawErrs) ? rawErrs : [];
    const errors = arr.map(e => ({
      membershipCategoryId: String(e['membershipCategoryId'] ?? e['MembershipCategoryId'] ?? ''),
      message: String(e['message'] ?? e['Message'] ?? ''),
    }));
    return { successCount, errors };
  }

  /** CSV file upload (same transport pattern as accounts dashboard import). */
  importCsvFile(propertyId: string, file: File): Observable<MembershipCategoryImportResult> {
    const uploadUrl = `${this.apiOrigin}${this.apiBasePath}/import/${propertyId}`;
    const headers = new HttpHeaders({ 'Skip-Content-Type': 'true' });
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<unknown>(uploadUrl, formData, { headers }).pipe(
      map(body => this.parseImportResponse(body)),
      catchError(err => this.handleImportHttpError(err)),
    );
  }

  private parseImportResponse(body: unknown): MembershipCategoryImportResult {
    const r = (body ?? {}) as Record<string, unknown>;
    const dataCandidate = r['data'] ?? r['Data'];
    const inner =
      dataCandidate && typeof dataCandidate === 'object'
        ? (dataCandidate as Record<string, unknown>)
        : (r as Record<string, unknown>);

    const importedCount = Number(inner['importedCount'] ?? inner['ImportedCount'] ?? 0);
    const raw = (inner['errors'] ?? inner['Errors']) as Array<Record<string, unknown>> | undefined;
    const arr = Array.isArray(raw) ? raw : [];
    const errors = arr
      .map(e => ({
        row: Number(e['row'] ?? e['Row'] ?? 0),
        message: String(e['message'] ?? e['Message'] ?? ''),
      }))
      .filter(e => e.row > 0 || e.message.trim().length > 0);

    return { importedCount, errors };
  }

  private handleImportHttpError(err: unknown): Observable<never> {
    const httpErr = err as HttpErrorResponse;
    const body = httpErr?.error;
    if (body && typeof body === 'object') {
      // Our APIs often return `{ errors: ApiError[] }` or `{ data: ..., errors: ApiError[] }`.
      const result = body as Record<string, unknown>;
      const raw =
        ((result['errors'] ?? result['Errors']) as Array<Record<string, unknown>> | undefined) ??
        (((result['error'] as Record<string, unknown> | undefined)?.['errors'] as Array<Record<string, unknown>> | undefined) ?? undefined);

      if (Array.isArray(raw) && raw.length > 0) {
        const errors = raw
          .map(e => ({
            row: Number(e['row'] ?? e['Row'] ?? 0),
            message: String(e['details'] ?? e['Details'] ?? e['message'] ?? e['Message'] ?? 'Import failed'),
          }))
          .filter(e => e.row > 0 || e.message.trim().length > 0);
        return throwError(() => ({ importErrors: errors.length ? errors : [{ row: 0, message: 'Import failed' }] }));
      }

      const msg = String(
        result['details'] ??
          result['Details'] ??
          result['message'] ??
          result['Message'] ??
          (result['error'] as Record<string, unknown> | undefined)?.['message'] ??
          httpErr.message ??
          'Import failed',
      );
      return throwError(() => ({ importErrors: [{ row: 0, message: msg }] }));
    }
    return throwError(() => httpErr?.error?.errors ?? httpErr);
  }

  private mapLevel(raw: unknown): MembershipLevel {
    const r = raw as Record<string, unknown>;
    const id = String(r['membershipCategoryId'] ?? r['MembershipCategoryId'] ?? '');
    const freqRaw = String(r['duesFrequency'] ?? r['DuesFrequency'] ?? '').trim();
    const statusRaw = String(r['status'] ?? r['Status'] ?? '').trim();
    return {
      membershipCategoryId: id,
      name: String(r['name'] ?? r['Name'] ?? ''),
      description: String(r['description'] ?? r['Description'] ?? ''),
      duesFrequency: (freqRaw || 'Monthly') as MembershipLevel['duesFrequency'],
      duesAmount: Number(r['duesAmount'] ?? r['DuesAmount'] ?? 0),
      activeMembers: Number(r['activeMembers'] ?? r['ActiveMembers'] ?? 0),
      status: (statusRaw || 'Active') as MembershipLevel['status'],
    };
  }

  private handleError(response: unknown) {
    const e = response as HttpErrorResponse;
    return throwError(() => e?.error?.errors ?? response);
  }
}
